import { Typography } from '@/components/atoms';
import { GUEST_PROJECTS_NEXT_PAGE_SKELETON_COUNT } from '@/core/api/services/guestProjectsService';
import { WELCOME_LOGIN_PATH } from '@/core/constants/appConfig';
import { CONTENTFUL_TAGS } from '@/core/constants/contentfulTags';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import {
  trackCatalogEvent,
  trackGuestLoginOverlayClick,
} from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { resolveGreetingPrefix } from '@/core/utils/greeting';
import { withWelcomeLoginQuery } from '@/modules/bloom-universal/constants/routes';
import { useScrollPagination } from '@/modules/properties-web/hooks/useScrollPagination';
import { markWelcomeOpenLogin } from '@/modules/welcome/utils/welcomeOpenLoginFlag';
import { router } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { GuestFloatingLoginButton } from '../components/GuestFloatingLoginButton';
import { GuestListEmptyState } from '../components/GuestListEmptyState';
import { GuestLoginPromptDialog } from '../components/GuestLoginPromptDialog';
import { GuestPropertyCard } from '../components/GuestPropertyCard';
import { GuestWebChrome } from '../components/GuestWebChrome';
import {
  GuestListScreenSkeleton,
  GuestPropertyCardSkeleton,
} from '../components/skeleton';
import { getGuestListLayout } from '../constants/guestLayout';
import { useGuestLabels } from '../hooks/useGuestLabels';
import { useGuestPropertiesList } from '../hooks/useGuestPropertiesList';

const NEXT_PAGE_SKELETON_KEYS = Array.from(
  { length: GUEST_PROJECTS_NEXT_PAGE_SKELETON_COUNT },
  (_, index) => `guest-next-skeleton-${index}`,
);

/**
 * Guest property list (public web) — paginated GET /v1/projects.
 * Logged-in users get WebAppLayout chrome; guests get GuestWebChrome.
 * Next-page load appends card shimmers under the existing grid (same as units).
 */
export function GuestPropertiesListScreen() {
  const colors = useUIColors();
  const { t } = useTranslation();
  const labels = useGuestLabels();
  const isAuthenticated = useAppStore((state) => state.auth.isAuthenticated);
  const [loginVisible, setLoginVisible] = useState(false);
  const { width } = useWindowDimensions();
  const layout = useMemo(() => getGuestListLayout(width), [width]);
  const {
    loadState,
    properties,
    canLoadMore,
    isPrefetchingCatalogue,
    loadMore,
    contentfulFetch,
    isContentfulSuccess,
  } = useGuestPropertiesList();
  const isSingleColumn = layout.columnCount === 1;
  /** WebAppLayout already insets the shell when authenticated. */
  const pagePadX = isAuthenticated ? 0 : layout.paddingHorizontal;

  const greeting = labels.greeting(
    resolveGreetingPrefix(t),
  );

  useEffect(() => {
    if (!isContentfulSuccess || !contentfulFetch) {
      return;
    }
    logger.info(
      'GuestPropertiesListScreen',
      'Contentful propertyImages response (guest tag)',
      {
        tag: contentfulFetch.tagId || CONTENTFUL_TAGS.GUEST,
        locale: contentfulFetch.locale,
        projectCount: contentfulFetch.projectCount,
        projectIds: contentfulFetch.projectIds,
        assets: contentfulFetch.assets,
        response: contentfulFetch.rawResponse,
      },
    );
  }, [isContentfulSuccess, contentfulFetch]);

  useCatalogDynatraceView(
    DT_SCREEN.GUEST,
    'guest_project_list_viewed',
    loadState === 'ready',
  );

  const openDetails = useCallback((id: string) => {
    trackCatalogEvent({
      screen: DT_SCREEN.GUEST,
      actionName: 'guest_project_card_click',
      eventType: 'action',
      properties: { projectId: id },
    });
    router.push(`/guest-details?id=${encodeURIComponent(id)}`);
  }, []);

  const openLogin = useCallback(() => {
    trackGuestLoginOverlayClick();
    setLoginVisible(true);
  }, []);
  const closeLogin = useCallback(() => setLoginVisible(false), []);
  const goLogin = useCallback(() => {
    setLoginVisible(false);
    // Same as Bloom Universal hub CTA: Welcome with LoginDialog open.
    const href = withWelcomeLoginQuery(WELCOME_LOGIN_PATH);
    markWelcomeOpenLogin();
    router.push(href as never);
  }, []);

  const scrollPagination = useScrollPagination({
    onLoadMore: loadMore,
    enabled: canLoadMore && loadState === 'ready',
  });

  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <GuestWebChrome headerTitle={labels.home}>
      <View style={[styles.root, { backgroundColor: colors.backgroundLevel2 }]}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.scrollContent,
            {
              paddingHorizontal: pagePadX,
              paddingTop: layout.isPhone ? 16 : 24,
              paddingBottom: layout.isPhone ? 96 : 48,
            },
          ]}
          showsVerticalScrollIndicator={false}
          {...scrollPagination}
        >
          {loadState === 'loading' ? (
            <GuestListScreenSkeleton
              columnWidth={layout.columnWidth}
              isSingleColumn={isSingleColumn}
            />
          ) : null}

          {loadState === 'empty' ? (
            <GuestListEmptyState title={labels.emptyNoProperties} />
          ) : null}

          {loadState === 'error' ? (
            <GuestListEmptyState title={labels.emptyFetchError} />
          ) : null}

          {loadState === 'ready' ? (
            <View style={styles.listBody}>
              <Typography variant="title1" color="primary">
                {greeting}
              </Typography>
              <View
                style={[styles.grid, isSingleColumn && styles.gridSingleColumn]}
              >
                {properties.map((property) => (
                  <View
                    key={property.id}
                    style={[
                      styles.gridItem,
                      isSingleColumn && styles.gridItemSingleColumn,
                      { width: layout.columnWidth },
                    ]}
                  >
                    <GuestPropertyCard
                      property={property}
                      onPress={() => openDetails(property.id)}
                      fullWidth
                    />
                  </View>
                ))}
                {isPrefetchingCatalogue
                  ? NEXT_PAGE_SKELETON_KEYS.map((skeletonKey) => (
                      <View
                        key={skeletonKey}
                        style={[
                          styles.gridItem,
                          isSingleColumn && styles.gridItemSingleColumn,
                          { width: layout.columnWidth },
                        ]}
                      >
                        <GuestPropertyCardSkeleton fullWidth />
                      </View>
                    ))
                  : null}
              </View>
            </View>
          ) : null}
        </ScrollView>

        {!isAuthenticated ? (
          <>
            <GuestFloatingLoginButton
              onPress={openLogin}
              isPhone={layout.isPhone}
              isTablet={layout.isTablet}
            />
            <GuestLoginPromptDialog
              visible={loginVisible}
              onMaybeLater={closeLogin}
              onLogin={goLogin}
            />
          </>
        ) : null}
      </View>
    </GuestWebChrome>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: '100%',
    minWidth: 0,
  },
  scroll: {
    flex: 1,
    width: '100%',
  },
  scrollContent: {
    width: '100%',
    maxWidth: '100%',
    flexGrow: 1,
    alignItems: 'stretch',
  },
  listBody: {
    gap: 24,
    width: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
    width: '100%',
    alignSelf: 'stretch',
  },
  gridSingleColumn: {
    marginHorizontal: 0,
  },
  gridItem: {
    paddingHorizontal: 8,
    marginBottom: 16,
    maxWidth: '100%',
    alignSelf: 'stretch',
    minWidth: 0,
    ...(Platform.OS === 'web'
      ? ({ display: 'flex', flexDirection: 'column' } as object)
      : {}),
  },
  gridItemSingleColumn: {
    paddingHorizontal: 0,
  },
});

export default GuestPropertiesListScreen;
